import axios from 'axios';
import { getToken } from '../../shared/utils/tokenStorage';

/**
 * Determine the current environment based on hostname and env vars
 * @returns {'development' | 'staging' | 'production'}
 */
const getEnvironment = () => {
  // Check environment variable first
  if (process.env.REACT_APP_ENV) {
    return process.env.REACT_APP_ENV;
  }

  const hostname = window.location.hostname;

  // Staging: staging.fleetcamagile.com
  if (hostname === 'staging.fleetcamagile.com') {
    return 'staging';
  }

  // Production: fleetcamagile.com, www.fleetcamagile.com, or AWS domains
  if (
    hostname === 'fleetcamagile.com' ||
    hostname === 'www.fleetcamagile.com' ||
    hostname.includes('amazonaws.com') ||
    hostname.includes('cloudfront.net')
  ) {
    return 'production';
  }

  // Default to development (localhost, 127.0.0.1, etc.)
  return 'development';
};

/**
 * Get the base API URL with fallbacks for different environments
 * This prevents hardcoded URLs and provides a consistent way to access API endpoints
 */
const getApiBaseUrl = () => {
  // First check for environment variable - highest priority
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const env = getEnvironment();
  const protocol = window.location.protocol; // Use same protocol as frontend

  if (env === 'production') {
    // Production: Use HTTPS and production domain
    return 'https://fleetcamagile.com/api/';
  }

  if (env === 'staging') {
    // Staging: Use staging subdomain
    return 'https://staging.fleetcamagile.com/api/';
  }

  // Development: Default to localhost
  return 'http://localhost:8000/api/';
};

/**
 * Get auth API URL with fallbacks
 */
const getAuthBaseUrl = () => {
  // First check for environment variable - highest priority
  if (process.env.REACT_APP_AUTH_URL) {
    const url = process.env.REACT_APP_AUTH_URL;
    return url.endsWith('/') ? url : `${url}/`;
  }

  const env = getEnvironment();

  if (env === 'production') {
    return 'https://fleetcamagile.com/api/v1/rbac/';
  }

  if (env === 'staging') {
    return 'https://staging.fleetcamagile.com/api/v1/rbac/';
  }

  return 'http://localhost:8000/api/v1/rbac/';
};

// Create separate Axios instances for API and Auth
const apiInstance = axios.create({
  baseURL: getApiBaseUrl(), // Base URL for non-auth APIs
});

const authBaseUrl = getAuthBaseUrl();

const authInstance = axios.create({
  baseURL: authBaseUrl, // Base URL for auth-related requests
});

// Add a request interceptor to the API instance to include the token in all requests
apiInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a request interceptor to the Auth instance (optional, if needed for Auth requests)
authInstance.interceptors.request.use(
  (config) => {
    // Don't add authorization header for login/registration endpoints
    const skipAuthPaths = ['/login/', '/register/', '/reset-password/', '/token/'];
    const shouldSkipAuth = skipAuthPaths.some((path) => config.url.includes(path));

    if (!shouldSkipAuth) {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Export separate functions for Auth and non-Auth requests
export const loginUser = (credentials) => {
  // Use the new RBAC login endpoint
  console.log('Login endpoint:', authInstance.defaults.baseURL + 'auth/login/');
  console.log('Credentials:', credentials);
  return authInstance.post('auth/login/', credentials);
};

export const getUserPermissions = () => {
  return authInstance.get('auth/user-info/');
};

// Function moved to top of file

// Function moved to default export

// Function moved to top of file

export { getApiBaseUrl, getAuthBaseUrl, getEnvironment };

export default {
  getApiBaseUrl,
  getAuthBaseUrl,
  getEnvironment,
  getApiUrl: (endpoint) => {
    const baseUrl = getApiBaseUrl();
    // Remove trailing slash from base URL if present and leading slash from endpoint if present
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

    return `${cleanBaseUrl}/${cleanEndpoint}`;
  },
};
